/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAgreementMagazines } from '@partiu/api/dashboard/hooks';
import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Image,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaDownload } from 'react-icons/fa6';
import { DashboardTemplate } from '../components/templates/DashboardTemplate';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';

function MagazineCard({
  magazine,
  onDownload,
  colorPrimary,
}: {
  magazine: any;
  onDownload: (url: string) => void;
  colorPrimary: string;
}) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('#ededed', 'gray.600');
  const titleColor = useColorModeValue('gray.800', 'gray.100');

  const title = magazine.articleTitle || magazine.title;

  return (
    <Box
      bg={cardBg}
      borderRadius="10px"
      p="8px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="rgba(100, 100, 111, 0.2) 0px 5px 8px 0px"
      transition="transform 0.25s ease-in-out"
      _hover={{ transform: 'scale(1.05)' }}
      position="relative"
      width="160px"
      display="flex"
      flexDirection="column"
    >
      <Box position="relative" width="100%">
        <Image
          src={magazine.cover}
          alt={title}
          width="100%"
          aspectRatio={0.7}
          objectFit="cover"
          borderRadius="5px"
          border="1px solid #ededed"
          backgroundColor="white"
          boxShadow="rgba(100, 100, 111, 0.2) 0px 5px 8px 0px"
        />

        <Badge
          colorScheme="orange"
          position="absolute"
          top="6px"
          left="6px"
          fontSize="xs"
          px={2}
          py="2px"
          borderRadius="6px"
          zIndex={2}
        >
          Revista
        </Badge>
      </Box>

      <VStack align="flex-start" spacing="2px" mt={2} width="100%">
        <Text
          fontSize="12px"
          fontWeight="bold"
          color={titleColor}
          width="100%"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          title={title}
        >
          {title}
        </Text>

        <Button
          size="xs"
          mt={1}
          width="100%"
          height="24px"
          px={2}
          fontSize="xs"
          leftIcon={<FaDownload />}
          color="white"
          backgroundColor={colorPrimary}
          _hover={{ backgroundColor: colorPrimary, opacity: 0.7 }}
          onClick={() => onDownload(magazine.fileUrl)}
        >
          Download
        </Button>
      </VStack>
    </Box>
  );
}

export function MagazinePage() {
  const { colors } = useTheme();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useAgreementMagazines({ page });

  function downloadFile(url: string) {
    window.open(url, '_blank');
  }

  if (isLoading)
    return (
      <DashboardTemplate>
        <Box width="100%" p={4}>
          <VStack mt={8}>
            <Spinner size="xl" />
            <Text>Carregando...</Text>
          </VStack>
        </Box>
      </DashboardTemplate>
    );
  if (error)
    return (
      <Alert status="error" mt={8}>
        <AlertIcon />
        Erro ao carregar magazines.
      </Alert>
    );

  return (
    <DashboardTemplate>
      <Box width="100%" p={4}>
        {data?.results?.length === 0 && (
          <Box
            width={'100%'}
            display={{ base: 'none', lg: 'flex' }}
            alignItems={'center'}
            justifyContent={'center'}
            mb={4}
          >
            <Text textAlign={'center'}>Nenhum magazine encontrado.</Text>
          </Box>
        )}

        <Box display="flex" flexWrap="wrap" gap={4} justifyContent={{ base: 'center', sm: 'flex-start' }}>
          {data?.results
            ?.filter((el: any) => el.cover)
            .map((magazine: any) => (
              <MagazineCard
                key={magazine.id}
                colorPrimary={colors.primaryColor}
                magazine={magazine}
                onDownload={downloadFile}
              />
            ))}
        </Box>
        <HStack spacing={2} justifyContent="space-between" pt={4} mt={6} borderTop="1px solid" borderColor="gray.100">
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={data && !data.previous}
            colorScheme="blue"
            variant="outline"
            size="sm"
            isLoading={isLoading}
          >
            ← Anterior
          </Button>
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.500">
              Página {page}
            </Text>
            {typeof data?.count === 'number' && (
              <Badge colorScheme="blue" borderRadius="full" px={2} fontSize="2xs">
                {data.count} {data.count === 1 ? 'revista' : 'revistas'}
              </Badge>
            )}
          </HStack>
          <Button
            onClick={() => setPage((p) => p + 1)}
            isDisabled={data && !data.next}
            colorScheme="blue"
            variant="outline"
            size="sm"
            isLoading={isLoading}
          >
            Próximo →
          </Button>
        </HStack>
      </Box>
    </DashboardTemplate>
  );
}
